import { Api } from '../services/api.ts';
import type { AtlasGlitch, GlitchIGU } from '../types/index.ts';

let glitchesCache: AtlasGlitch[] = [];

export function initGlitch(onGlitchesUpdate: (glitches: GlitchIGU[]) => void) {
  const tableBody = document.querySelector('#table-glitches tbody') as HTMLTableSectionElement | null;
  const inM = document.getElementById('glitch-m') as HTMLSelectElement | null;
  const inF = document.getElementById('glitch-f') as HTMLSelectElement | null;
  const inS = document.getElementById('glitch-s') as HTMLSelectElement | null;
  const inR = document.getElementById('glitch-r') as HTMLSelectElement | null;
  const livePreview = document.getElementById('live-igu-preview') as HTMLElement | null;
  const btnAdd = document.getElementById('btn-add-glitch') as HTMLButtonElement | null;
  const btnExportCsv = document.getElementById('btn-export-csv') as HTMLButtonElement | null;

  function calcLiveIGU(): number {
    const m = parseFloat(inM?.value || '0');
    const f = parseFloat(inF?.value || '0');
    const s = parseFloat(inS?.value || '0');
    const r = parseFloat(inR?.value || '0');
    const res = (m + f + s) / (1 + r);
    if (livePreview) {
      livePreview.innerHTML = `Vista previa IGU: <strong style="color:var(--primary); font-size:1.15rem;">${res.toFixed(2)}</strong>`;
    }
    return res;
  }

  [inM, inF, inS, inR].forEach(el => el?.addEventListener('input', calcLiveIGU));

  async function loadGlitches() {
    try {
      glitchesCache = await Api.getGlitches();
      render();
    } catch (err) {
      console.error('Error al cargar glitches:', err);
    }
  }

  function render() {
    if (!tableBody) return;
    tableBody.innerHTML = '';

    glitchesCache.forEach((g) => {
      const tr = document.createElement('tr');
      const iguVal = g.igu ?? g.indice_igu ?? 0;
      const iguClass = iguVal >= 3.0 ? 'igu-high' : (iguVal >= 2.0 ? 'igu-mid' : 'igu-mitigated');

      tr.innerHTML = `
        <td><strong>${escapeHtml(g.caso_estudio || g.caso_nombre || '')}</strong></td>
        <td><span style="font-family:var(--font-mono); font-size:0.78rem;">${escapeHtml(g.estacion || g.name || '')}</span></td>
        <td><span style="font-size:0.74rem; font-weight:600; color:var(--primary);">${escapeHtml(g.dimension || '')}</span></td>
        <td>${g.m ?? g.ruptura_m ?? 0}</td>
        <td>${g.f ?? g.fenomenologia_f ?? 0}</td>
        <td>${g.s ?? g.simbolico_s ?? 0}</td>
        <td><span style="color:var(--tactics-res); font-weight:700;">${g.r ?? g.tactica_r ?? 0}</span></td>
        <td><span class="igu-score ${iguClass}">${iguVal.toFixed(2)}</span></td>
        <td style="font-size:0.78rem; color:#334155;">${escapeHtml(g.descripcion || g.desc_fenomenologica || '')}</td>
        <td style="text-align:center;">
          <button data-del-id="${g.id}" class="btn-del" title="Eliminar registro de glitch">
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><polyline points="3 6 5 6 21 6"/><path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"/><line x1="10" y1="11" x2="10" y2="17"/><line x1="14" y1="11" x2="14" y2="17"/></svg>
          </button>
        </td>
      `;
      tableBody.appendChild(tr);
    });

    tableBody.querySelectorAll('button[data-del-id]').forEach(btn => {
      btn.addEventListener('click', async () => {
        const id = btn.getAttribute('data-del-id');
        if (!id) return;
        if (confirm('¿Eliminar este registro de glitch urbano en SQLite?')) {
          try {
            await Api.deleteGlitch(id);
            glitchesCache = glitchesCache.filter(g => g.id.toString() !== id);
            render();
          } catch (err) {
            alert('Error al eliminar glitch de SQLite');
          }
        }
      });
    });

    onGlitchesUpdate(glitchesCache);
  }

  btnAdd?.addEventListener('click', async () => {
    const stationInput = document.getElementById('glitch-station') as HTMLInputElement | null;
    const caseInput = document.getElementById('glitch-case') as HTMLSelectElement | null;
    const dimensionInput = document.getElementById('glitch-dimension') as HTMLSelectElement | null;
    const descInput = document.getElementById('glitch-desc') as HTMLTextAreaElement | null;

    const station = stationInput?.value.trim();
    if (!station) {
      alert('Por favor indica la estación del transecto.');
      return;
    }

    const m = parseFloat(inM?.value || '0');
    const f = parseFloat(inF?.value || '0');
    const s = parseFloat(inS?.value || '0');
    const r = parseFloat(inR?.value || '0');
    const caso_estudio = caseInput?.value || 'El Raval';
    const dimension = dimensionInput?.value || 'fisico';
    const descripcion = descInput?.value.trim() || 'Discontinuidad registrada en transecto.';

    try {
      await Api.addGlitch({
        caso_estudio,
        name: station,
        estacion: station,
        dimension,
        ruptura_m: m,
        fenomenologia_f: f,
        simbolico_s: s,
        tactica_r: r,
        desc_fenomenologica: descripcion
      });

      if (stationInput) stationInput.value = '';
      if (descInput) descInput.value = '';
      calcLiveIGU();
      await loadGlitches();
    } catch (err) {
      alert('Error al registrar glitch en SQLite');
    }
  });

  // Exportar CSV para QGIS
  btnExportCsv?.addEventListener('click', () => {
    if (glitchesCache.length === 0) {
      alert('No hay registros de glitches para exportar.');
      return;
    }

    const headers = ['id', 'caso_estudio', 'estacion', 'dimension', 'm', 'f', 's', 'r', 'igu', 'descripcion'];
    const rows = glitchesCache.map(g => [
      `"${g.id}"`,
      `"${g.caso_estudio || g.caso_nombre || ''}"`,
      `"${g.estacion || g.name || ''}"`,
      `"${g.dimension}"`,
      g.m ?? g.ruptura_m ?? 0,
      g.f ?? g.fenomenologia_f ?? 0,
      g.s ?? g.simbolico_s ?? 0,
      g.r ?? g.tactica_r ?? 0,
      (g.igu ?? g.indice_igu ?? 0).toFixed(2),
      `"${(g.descripcion || g.desc_fenomenologica || '').replace(/"/g, '""')}"`
    ]);

    const csvContent = '\uFEFF' + [headers.join(','), ...rows.map(e => e.join(','))].join('\n');
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `Glitches_IGU_QGIS_${new Date().toISOString().slice(0, 10)}.csv`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
  });

  loadGlitches();
}

function escapeHtml(str: string): string {
  const div = document.createElement('div');
  div.innerText = str || '';
  return div.innerHTML;
}
